import { message } from 'antd';
import config from 'config';
import {
  LabworkListSort,
  LabworkOutput,
  LabworkStatus,
  useGetLabworkListQuery,
  useRemoveLabworkMutation,
  useUpdateLabworkMutation,
  useUpdateLabworkStatusMutation,
} from 'gql/types';
import { useSort } from 'modules/common/hooks';
import { GET_LABWORK_LIST } from 'modules/donor/graphql/queries';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useTranslation } from 'react-i18next';

export const useDonorLabwork = (donorId: string | undefined) => {
  const { t } = useTranslation('donor.DonorLabwork');
  const pagination = usePagination();
  const sort = useSort<LabworkListSort>();

  const labworkListVariables = {
    input: {
      take: config.PAGINATION.PAGE_SIZE,
      skip: pagination.skip,
      sort: sort.options,
      filter: {
        donorId,
      },
    },
  };

  const getLabworkQuery = useGetLabworkListQuery({
    variables: labworkListVariables,
    onCompleted: (result) => {
      pagination.setTotal(result.getLabworkList.meta.total);
    },
    skip: !Boolean(donorId),
  });

  const [updateLabwork, updateLabworkMutation] = useUpdateLabworkMutation({
    refetchQueries: [
      { query: GET_LABWORK_LIST, variables: labworkListVariables },
    ],
    onCompleted: () => {
      message.success(t('labworkHasBeenSuccessfullyUpdated'));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const [removeLabwork, removeLabworkMutation] = useRemoveLabworkMutation({
    refetchQueries: [
      { query: GET_LABWORK_LIST, variables: labworkListVariables },
    ],
    onCompleted: () => {
      message.success(t('labworkHasBeenDeleted'));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const [updateLabworkStatus, updateLabworkStatusMutation] =
    useUpdateLabworkStatusMutation({
      refetchQueries: [
        { query: GET_LABWORK_LIST, variables: labworkListVariables },
      ],
      onCompleted: () => {
        message.success(t('labworkHasBeenSuccessfullyUpdated'));
      },
      onError: (error) => {
        message.error(error.message);
      },
    });

  const updateLabworkHandler = ({
    id,
    donor,
    collectionDate,
  }: LabworkOutput) => {
    updateLabwork({
      variables: {
        input: {
          id,
          donorId: donor.id,
          collectionDate,
        },
      },
    });
  };

  const removeLabworkHandler = (id: string) => {
    removeLabwork({
      variables: {
        input: { id },
      },
      update(cache) {
        cache.evict({ fieldName: 'getLabworkList' });
      },
    });
  };

  const updateLabworkStatusHandler = (id: string, status: LabworkStatus) => {
    updateLabworkStatus({
      variables: {
        input: {
          id,
          status,
        },
      },
    });
  };

  return {
    labworks: getLabworkQuery.data?.getLabworkList.data as LabworkOutput[],
    isLoading: [
      getLabworkQuery,
      updateLabworkMutation,
      updateLabworkStatusMutation,
      removeLabworkMutation,
    ].some(({ loading }) => loading),
    updateLabworkHandler,
    removeLabworkHandler,
    updateLabworkStatusHandler,
    pagination,
    sort,
  };
};
